import React, { useMemo, useState } from 'react';
import { 
    Box, 
    Text, 
    Grid, 
    HStack, 
    VStack, 
    Divider, 
    Badge, 
    Flex, 
    Table, 
    Tbody, 
    Tr, 
    Td, 
    Button,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    Tooltip,
    Icon
} from '@chakra-ui/react';
import { FaInfoCircle } from 'react-icons/fa';
import { useGame } from '../../context/GameContext';

// Symbol icons mapping with updated probabilities and payouts from the proposal
const symbolIcons = {
    // Regular symbols
    Cherry: { emoji: '🍒', color: '#FF5252', type: 'Common', payout: 1.0, probability: '1 in 5' },
    Lemon: { emoji: '🍋', color: '#FFEB3B', type: 'Common', payout: 2.0, probability: '1 in 12' },
    Apple: { emoji: '🍎', color: '#F44336', type: 'Common', payout: 3.0, probability: '1 in 25' },
    Blueberry: { emoji: '🫐', color: '#3F51B5', type: 'Common', payout: 4.0, probability: '1 in 35' },
    Banana: { emoji: '🍌', color: '#FFEB3B', type: 'Uncommon', payout: 5.0, probability: '1 in 50' },
    Watermelon: { emoji: '🍉', color: '#4CAF50', type: 'Uncommon', payout: 10.0, probability: '1 in 100' },
    Orange: { emoji: '🍊', color: '#FF9800', type: 'Rare', payout: 15.0, probability: '1 in 200' },
    Grape: { emoji: '🍇', color: '#9C27B0', type: 'Rare', payout: 25.0, probability: '1 in 400' },
    GreenApple: { emoji: '🍏', color: '#8BC34A', type: 'Epic', payout: 40.0, probability: '1 in 800' },
    Aubergine: { emoji: '🍆', color: '#673AB7', type: 'Epic', payout: 75.0, probability: '1 in 1,500' },
    Crown: { emoji: '👑', color: '#FFC107', type: 'Epic', payout: 125.0, probability: '1 in 1,900' },
    Mushroom: { emoji: '🍄', color: '#F44336', type: 'Epic', payout: 250.0, probability: '1 in 3,800' },
    Star: { emoji: '⭐', color: '#FFC107', type: 'Legendary', payout: 500.0, probability: '1 in 7,600' },
    Bell: { emoji: '🔔', color: '#FFC107', type: 'Legendary', payout: 1000.0, probability: '1 in 14,250' },
    Clove: { emoji: '🍀', color: '#4CAF50', type: 'Mythic', payout: 5000.0, probability: '1 in 28,500' },
    Peppers: { emoji: '🌶️', color: '#F44336', type: 'Mythic', payout: 10000.0, probability: '1 in 47,500' },
    
    // Special bonus symbols
    Ticket: { emoji: '🎟️', color: '#4CAF50', type: 'Bonus', payout: 0, probability: '1 in 2' },
    Chest: { emoji: '🎁', color: '#FFD700', type: 'Bonus', payout: 0, probability: '1 in 950' }
};

const SymbolMultiplierDisplay = () => {
    const { gameState, getGameSymbols, getMatchMultipliers } = useGame();
    
    // Get game data from context
    const gameSymbols = getGameSymbols ? getGameSymbols() : [];
    const matchMultipliers = getMatchMultipliers ? getMatchMultipliers() : {};
    
    // Destructure game state with defaults
    const { 
        cards = [], 
        revealedCards = Array(12).fill(false), 
        betAmount = 1,
        winAmount = 0,
        matches = []
    } = gameState || {};
    
    // Calculate symbol counts and payouts
    const symbolData = useMemo(() => {
        // Object to store counts for each symbol
        const counts = {};
        
        // Count revealed symbols
        cards.forEach((card, index) => {
            if (revealedCards[index] && card.symbol) {
                counts[card.symbol] = (counts[card.symbol] || 0) + 1;
            }
        });
        
        // Find the symbol with the highest multiplier (3+ matches)
        let highestMultiplierSymbol = null;
        let highestMultiplier = 0;
        let highestCount = 0;
        
        Object.entries(counts).forEach(([symbol, count]) => {
            if (count >= 3) {
                const symbolInfo = gameSymbols.find(s => s.symbol === symbol);
                if (symbolInfo && symbolInfo.type !== 'Bonus' && symbolInfo.type !== 'Wildcard') {
                    // Base payout
                    const basePayout = symbolInfo.payout;
                    
                    // Extra symbol bonus: +0.1 per extra match after 3
                    const extraBonus = Math.max(0, (count - 3) * 0.1);
                    
                    // Total multiplier = base payout + extra bonus
                    const totalMultiplier = basePayout + extraBonus;
                    
                    if (totalMultiplier > highestMultiplier || 
                        (totalMultiplier === highestMultiplier && count > highestCount)) {
                        highestMultiplierSymbol = symbol;
                        highestMultiplier = totalMultiplier;
                        highestCount = count;
                    }
                }
            }
        });
        
        // Format the data for display
        const formattedData = Object.entries(counts).map(([symbol, count]) => {
            const symbolInfo = gameSymbols.find(s => s.symbol === symbol) || {};
            const isBonus = symbolInfo.type === 'Bonus' || symbolInfo.type === 'Wildcard';
            
            // Calculate extra bonus for regular symbols
            const extraBonus = !isBonus && count >= 3 ? Math.max(0, (count - 3) * 0.1) : 0;
            
            // Total multiplier = base payout + extra bonus
            const totalMultiplier = !isBonus && count >= 3 ? symbolInfo.payout + extraBonus : 0;
            
            // Final payout
            const payout = !isBonus && count >= 3 ? totalMultiplier * betAmount : 0;
            
            const isHighestMultiplier = symbol === highestMultiplierSymbol;
            
            return {
                symbol,
                count,
                isBonus,
                baseMultiplier: symbolInfo.payout || 0,
                extraBonus,
                totalMultiplier,
                payout,
                symbolInfo,
                isHighestMultiplier
            };
        });
        
        // Sort by count (descending), then by payout (descending)
        return formattedData.sort((a, b) => {
            if (a.isHighestMultiplier && !b.isHighestMultiplier) return -1;
            if (!a.isHighestMultiplier && b.isHighestMultiplier) return 1;
            if (a.count !== b.count) return b.count - a.count;
            return b.payout - a.payout;
        });
    }, [cards, revealedCards, gameSymbols, matchMultipliers, betAmount]);
    
    // Determine if there's a multiplier win
    const hasMultiplierWin = useMemo(() => {
        return symbolData.some(item => !item.isBonus && item.count >= 3);
    }, [symbolData]);
    
    // Calculate bonus activation
    const bonusActivation = useMemo(() => {
        // Check for bonus symbols
        const chestSymbol = symbolData.find(item => item.symbol === 'Chest');
        const wheelSymbol = symbolData.find(item => item.symbol === 'Wheel');
        
        if (wheelSymbol && wheelSymbol.count > 0) {
            return { active: true, type: 'Wheel', count: wheelSymbol.count };
        } else if (chestSymbol && chestSymbol.count > 0) {
            return { active: true, type: 'Chest', count: chestSymbol.count };
        }
        
        return { active: false, type: null };
    }, [symbolData]);
    
    // If no cards are revealed yet, show a placeholder
    if (revealedCards.every(revealed => !revealed)) {
        return (
            <Box
                mt={4}
                p={4}
                bg="whiteAlpha.200"
                borderRadius="lg"
                w="100%"
                maxW={{ base: "100%", md: "600px" }}
                mx="auto"
                borderColor="purple.400"
                borderWidth="1px"
                boxShadow="0 0 10px rgba(128, 90, 213, 0.3)"
                textAlign="center"
            >
                <Text fontSize="lg" fontWeight="bold" mb={3} color="purple.300">
                    Symbol Counts & Multipliers
                </Text>
                <Text color="gray.400" fontSize="md">
                    Flip cards to reveal symbols and calculate payouts
                </Text>
                <Divider my={3} />
                <Text fontSize="xs" color="gray.400" textAlign="center">
                    3+ matching symbols trigger a multiplier win. Each additional symbol adds +0.1 to the multiplier.
                </Text>
            </Box>
        );
    }
    
    return (
        <Box
            mt={4}
            p={4}
            bg="whiteAlpha.200"
            borderRadius="lg"
            w="100%"
            maxW={{ base: "100%", md: "600px" }}
            mx="auto"
            borderColor="purple.400"
            borderWidth="1px"
            boxShadow="0 0 10px rgba(128, 90, 213, 0.3)"
        >
            <Text fontSize="lg" fontWeight="bold" mb={3} textAlign="center" color="purple.300">
                Symbol Counts & Multipliers
            </Text>
            
            {/* Game Rules Summary */}
            <Box mb={4} p={2} bg="whiteAlpha.100" borderRadius="md">
                <Text fontSize="xs" color="gray.400" textAlign="center">
                    3+ matching symbols = win • Each extra symbol adds +0.1 to multiplier • Collect tickets with any win
                </Text>
            </Box>
            
            {/* Regular Symbols Grid */}
            <Grid 
                templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }} 
                gap={3} 
                mb={4}
            >
                {symbolData
                    .filter(item => !item.isBonus && item.count > 0)
                    .map(({ symbol, count, baseMultiplier, extraBonus, totalMultiplier, payout, isHighestMultiplier }) => {
                        const icon = symbolIcons[symbol] || {};
                        const hasMatch = count >= 3;
                        
                        return (
                            <Box 
                                key={symbol} 
                                p={3} 
                                bg={hasMatch 
                                    ? isHighestMultiplier 
                                        ? "green.700" 
                                        : "green.800" 
                                    : "gray.700"
                                }
                                borderRadius="md"
                                borderWidth={isHighestMultiplier ? "2px" : "1px"}
                                borderColor={isHighestMultiplier ? "yellow.300" : "transparent"}
                                opacity={hasMatch ? 1 : 0.8}
                                position="relative"
                            >
                                {isHighestMultiplier && hasMatch && (
                                    <Badge 
                                        position="absolute" 
                                        top="-10px" 
                                        right="-10px" 
                                        colorScheme="yellow"
                                        borderRadius="full"
                                        px={2}
                                    >
                                        Best
                                    </Badge>
                                )}
                                
                                <HStack justify="space-between" mb={1}>
                                    <HStack>
                                        <Text fontSize="xl">{icon.emoji}</Text>
                                        <Text fontWeight="bold">{symbol}</Text>
                                    </HStack>
                                    <Text fontWeight="bold">{count}×</Text>
                                </HStack>
                                
                                {hasMatch && (
                                    <>
                                        <Divider my={1} />
                                        <HStack justify="space-between" fontSize="sm">
                                            <Text>Base:</Text>
                                            <Text>{baseMultiplier.toFixed(1)}×</Text>
                                        </HStack>
                                        {extraBonus > 0 && (
                                            <HStack justify="space-between" fontSize="sm">
                                                <Text>Extra ({count-3}):</Text>
                                                <Text>+{extraBonus.toFixed(1)}</Text>
                                            </HStack>
                                        )}
                                        <HStack justify="space-between" fontSize="sm" fontWeight="bold" color="green.300">
                                            <Text>Total:</Text>
                                            <Text>{totalMultiplier.toFixed(1)}×</Text>
                                        </HStack>
                                        <HStack justify="space-between" fontSize="sm" fontWeight="bold" color="yellow.300">
                                            <Text>Payout:</Text>
                                            <Text>{payout.toFixed(1)} JINU</Text>
                                        </HStack>
                                    </>
                                )}
                            </Box>
                        );
                    })
                }
            </Grid>
            
            {/* Bonus Activation */}
            {bonusActivation.active && (
                <Box 
                    p={3} 
                    bg="purple.800" 
                    borderRadius="md" 
                    mb={4}
                    borderWidth="2px"
                    borderColor="yellow.300"
                >
                    <HStack justify="space-between" mb={2}>
                        <HStack>
                            <Text fontSize="xl">{symbolIcons[bonusActivation.type]?.emoji}</Text>
                            <Text fontWeight="bold">
                                {bonusActivation.type === 'Wheel' ? 'Wheel of Fortune' : 'Chest Bonus'} Activated!
                            </Text>
                        </HStack>
                        <Badge colorScheme="yellow">Bonus</Badge>
                    </HStack>
                    
                    <Divider my={2} />
                    
                    <Text fontSize="sm" color="gray.300" textAlign="center">
                        {bonusActivation.type === 'Wheel' ? 
                            'Wheel of Fortune activated! Spin for a chance to win big prizes.' : 
                            'Chest bonus game activated! Select a chest to reveal your prize.'}
                    </Text>
                </Box>
            )}
            
            {/* Total Payout */}
            <HStack justify="space-between" fontWeight="bold">
                <Text>Total Payout:</Text>
                <Text color="green.300" fontSize="xl">{winAmount.toFixed(1)} JINU</Text>
            </HStack>
            
            {/* Game Rules Reference */}
            <Box mt={4} p={2} bg="whiteAlpha.100" borderRadius="md">
                <Text fontSize="xs" fontWeight="bold" mb={1} color="gray.300">
                    Game Rules:
                </Text>
                <Text fontSize="xs" color="gray.400">
                    • 3+ matching symbols trigger a multiplier win
                </Text>
                <Text fontSize="xs" color="gray.400">
                    • If multiple wins occur, only the highest multiplier applies
                </Text>
                <Text fontSize="xs" color="gray.400">
                    • Each additional matching symbol (after 3) adds +0.1 to the multiplier
                </Text>
                <Text fontSize="xs" color="gray.400">
                    • Collect tickets with any win and use them on the Wheel of Fortune
                </Text>
            </Box>
        </Box>
    );
};

export default SymbolMultiplierDisplay;
